import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    logger.log('[AuthContext] Initializing auth state...');

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      logger.log('[AuthContext] Initial session:', session ? 'Active' : 'None');
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        logger.log('[AuthContext] Auth event received:', event);

        // Ignore redundant SIGNED_IN events that don't change the session
        // This prevents unnecessary re-renders during Android file picker operations
        if (event === 'SIGNED_IN') {
          setSession((currentSession) => {
            // If we already have this exact session, don't update (prevents re-render)
            if (currentSession?.access_token === newSession?.access_token) {
              logger.log('[AuthContext] Ignoring redundant SIGNED_IN event - session unchanged');
              return currentSession;
            }
            logger.log('[AuthContext] Session changed, updating state');
            return newSession;
          });
          setUser(newSession?.user ?? null);
        } else {
          // For other events (SIGNED_OUT, TOKEN_REFRESHED, etc.), always update
          logger.log('[AuthContext] Processing auth event:', event);
          setSession(newSession);
          setUser(newSession?.user ?? null);
        }

        setLoading(false);
      }
    );

    return () => {
      logger.log('[AuthContext] Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    logger.log('[AuthContext] Signing up user');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/email-confirmation`
      }
    });

    if (error) {
      logger.error('[AuthContext] Sign up error', error);
      throw error;
    }

    logger.log('[AuthContext] Sign up successful');
  };

  const signIn = async (email: string, password: string) => {
    logger.log('[AuthContext] Signing in user');
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      logger.error('[AuthContext] Sign in error', error);
      throw error;
    }

    logger.log('[AuthContext] Sign in successful');
  };

  const signOut = async () => {
    logger.log('[AuthContext] Signing out user');
    const { error } = await supabase.auth.signOut();
    if (error) {
      logger.error('[AuthContext] Sign out error', error);
      throw error;
    }
    logger.log('[AuthContext] Sign out successful');
  };

  const resetPassword = async (email: string) => {
    logger.log('[AuthContext] Requesting password reset');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });

    if (error) {
      logger.error('[AuthContext] Password reset error', error);
      throw error;
    }

    logger.log('[AuthContext] Password reset email sent');
  };

  const updatePassword = async (newPassword: string) => {
    logger.log('[AuthContext] Updating password');
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      logger.error('[AuthContext] Update password error', error);
      throw error;
    }

    logger.log('[AuthContext] Password updated successfully');
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut,
      resetPassword,
      updatePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
